import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  ContentWrapper,
  ProjectTitle,
  SubSectionHeader,
  InfoItem,
  ArchivedBadge,
  ListItemText,
  ListItemTextResposta,
  ProjectContent,
  AreasContainer,
  AreaHeader,
  AreaName,
  ResponsiblesContainer,
  ResponsibleItem,
  ResponsibleLabel,
  ResponsibleValue,
  AreaInfoGrid,
  AreaInfoCard,
  AreaInfoLabel,
  AreaInfoValue,
  AreaSeparator,
  NoAreasMessage,
  InfoCardsGrid,
  InfoCard,
  InfoCardLabel,
  InfoCardValue,
  StyledSpan,
  HubStatusBanner,
  HubSectionBalloon,
  HubTeamGrid,
  HubTeamCard,
  HubTeamName,
  HubTeamRoles,
  HubTeamMeta,
  HubDownloadsList,
  HubDownloadLink,
  HubDownloadCategoryTitle,
  HubAreasLinkList,
  HubAreaBlock,
  HubAreaLink,
  HubContactLine,
  HubListTable,
  HubListTableScroll,
  HubBadge,
} from "./styles";
import {
  returnYesorNoforBoolean,
  formatSICARCode,
  convertPhone,
  transformNumbersToHectares,
  getProjectOwnerDocumento,
} from "../../../constants/functions";
import PasswordModal from "../../../components/projects_t_consolidation/password_modal";
import RequestModal from "../../../components/RequestModal";
import {
  formatHubRoleLabels,
  getHubSummaryCards,
  groupHubDownloads,
  groupAreaHubDownloads,
  mergeProjectWithHub,
} from "../../../utils/projectHub";
import {
  emptyHubMap,
  enrichHubMapFromScans,
  isAlertMapSource,
  isAlertRequestMapSource,
} from "../../../utils/projectHubMapUtils";
import { openAlertFromAlertRequest } from "../../../utils/openAlertFromAlertRequest";
import { buildProjectContactMailtoHref } from "../../../utils/contactMailto";
import {
  getAlertRequestSearchErrorMessage,
  getAlertSearchErrorMessage,
  normalizeAlertCode,
  openAlertByCode,
} from "../../../utils/guardianAlertByCode";
import { guardianAlertService } from "../../../services/guardianAlertService";
import { canCreateGuardianAlert } from "../../../utils/userRoles";
import { appStatus } from "../../../store/modules/app_status/actions";
import AlertRequestResolutionBadge from "../../../components/guardian/AlertRequestResolutionBadge";
import ProjectHubMapSection from "./map/ProjectHubMapSection";
import AreaCameraCarousel from "./AreaCameraCarousel";
import { MapCodeButton } from "./map/styles";
import Swal from "sweetalert2";

function formatHubDateTime(value) {
  if (!value) return "—";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("pt-BR");
  } catch {
    return "—";
  }
}

const Intel = ({ user, project, hub = null, hubLoading = false, hubError = null }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [openingAlertId, setOpeningAlertId] = useState(null);
  const [loadingMapCode, setLoadingMapCode] = useState(null);
  const [detailRequest, setDetailRequest] = useState(null);
  const [requestOverrides, setRequestOverrides] = useState({});

  const showCreateAlert = canCreateGuardianAlert(user);

  const displayProject = useMemo(
    () => mergeProjectWithHub(project, hub),
    [project, hub]
  );

  const projectOwner =
    hub?.people?.owner || displayProject?.owner_details || {};
  const hasAreas =
    displayProject?.guardian_areas && displayProject.guardian_areas.length > 0;

  const summaryCards = useMemo(
    () => getHubSummaryCards(hub?.summary || {}),
    [hub]
  );

  const team = Array.isArray(hub?.people?.team) ? hub.people.team : [];
  const projectTitle =
    displayProject?.title || displayProject?.name || project?.title || "";
  const downloads = Array.isArray(hub?.downloads) ? hub.downloads : [];
  const reports = Array.isArray(hub?.reports) ? hub.reports : [];
  const hubMap = useMemo(
    () =>
      enrichHubMapFromScans(
        hub?.map || emptyHubMap(),
        hub?.firms_scans || [],
        hub?.guardian_areas || []
      ),
    [hub?.map, hub?.firms_scans, hub?.guardian_areas]
  );
  const geographicCoordinates = Array.isArray(hub?.geographic_coordinates)
    ? hub.geographic_coordinates
    : [];
  const alerts = Array.isArray(hub?.alerts) ? hub.alerts : [];
  const alertRequests = useMemo(() => {
    const raw = Array.isArray(hub?.alert_requests) ? hub.alert_requests : [];
    return raw.map((req) => {
      const override = req?.id ? requestOverrides[req.id] : null;
      return override ? { ...req, ...override } : req;
    });
  }, [hub?.alert_requests, requestOverrides]);
  const pendingRequests = alertRequests.filter((r) => r.resolution == null);

  /** Downloads sem relatórios PDF (relatórios ficam na seção própria) */
  const downloadGroups = useMemo(
    () =>
      groupHubDownloads(downloads, []).filter((group) => group.key !== "report"),
    [downloads]
  );

  const reportItems = useMemo(() => {
    const fromHubReports = reports
      .map((report) => {
        const href = report.download_url || report.file_url;
        if (!href) return null;
        const areaSuffix = report.guardian_area_name
          ? ` — ${report.guardian_area_name}`
          : "";
        return {
          key: `report-${report.id || href}`,
          href,
          title: `${report.file_name || "Relatório PDF"}${areaSuffix}`,
          badge: "PDF",
        };
      })
      .filter(Boolean);

    const fromDownloads = groupHubDownloads(downloads, [])
      .filter((group) => group.key === "report")
      .flatMap((group) => group.items);

    return [...fromDownloads, ...fromHubReports];
  }, [downloads, reports]);

  const getConsolidatedAreaInfo = () => {
    if (!hasAreas) return null;

    const totalArea = displayProject.guardian_areas.reduce(
      (sum, area) => sum + (parseFloat(area.total_area) || 0),
      0
    );

    const legalReserveArea = displayProject.guardian_areas.reduce(
      (sum, area) => sum + (parseFloat(area.legal_reserve_area) || 0),
      0
    );

    const hasDeficit = displayProject.guardian_areas.some(
      (area) =>
        area.reserve_legal_status?.toLowerCase().includes("deficit") ||
        area.reserve_legal_status?.toLowerCase().includes("déficit")
    );

    return {
      totalArea,
      legalReserveArea,
      hasDeficit,
      areaCount: displayProject.guardian_areas.length,
    };
  };

  const areaInfo = getConsolidatedAreaInfo();

  const closePasswordModal = () => {
    setIsPasswordModalOpen(false);
  };

  const handleOpenCreatedAlert = async (req) => {
    if (!req?.id || req.resolution !== "created") return;
    if (openingAlertId) return;
    try {
      setOpeningAlertId(req.id);
      await openAlertFromAlertRequest(dispatch, navigate, req);
    } catch (error) {
      console.error("Erro ao abrir alerta da solicitação:", error);
      await Swal.fire({
        title: "Não foi possível abrir o alerta",
        text:
          error?.message ||
          "Não encontramos o alerta vinculado a esta solicitação.",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setOpeningAlertId(null);
    }
  };

  const upsertRequestOverride = (updated) => {
    if (!updated?.id) return;
    setRequestOverrides((prev) => ({
      ...prev,
      [updated.id]: { ...(prev[updated.id] || {}), ...updated },
    }));
    setDetailRequest((prev) =>
      prev?.id === updated.id ? { ...prev, ...updated } : prev
    );
  };

  const resolveAlertRequestByCode = async (rawCode) => {
    const code = normalizeAlertCode(rawCode);
    const fromHub = alertRequests.find(
      (req) => normalizeAlertCode(req.cod_alerta) === code
    );
    if (fromHub) return fromHub;

    const data = await guardianAlertService.getAlertRequestByCode(code);
    if (!data?.success || !data?.alert_request) {
      throw new Error("Código não encontrado. Confira o e-mail ou a lista.");
    }
    return data.alert_request;
  };

  const handleMapPointCodeClick = async (point) => {
    const code = String(point?.label || "").trim();
    if (!code || loadingMapCode) return;

    try {
      setLoadingMapCode(code);

      if (isAlertMapSource(point.source)) {
        await openAlertByCode(
          dispatch,
          navigate,
          code,
          guardianAlertService.getAlertByCode
        );
        return;
      }

      if (isAlertRequestMapSource(point.source)) {
        const req = await resolveAlertRequestByCode(code);
        setDetailRequest(req);
        return;
      }
    } catch (error) {
      console.error("Erro ao abrir código do mapa:", error);
      const fallback =
        error?.message || "Tente novamente em instantes.";
      const message = error?.response
        ? isAlertMapSource(point.source)
          ? getAlertSearchErrorMessage(error)
          : getAlertRequestSearchErrorMessage(error)
        : fallback;
      await Swal.fire({
        title: "Não foi possível abrir",
        text: message,
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setLoadingMapCode(null);
    }
  };

  const handleCreateAlertFromRequest = (req) => {
    if (!showCreateAlert || !req) return;
    setDetailRequest(null);
    dispatch(appStatus("Guardião"));
    navigate("/guardian", {
      state: { activeTab: "new_alert", alertRequest: req },
    });
  };

  const handleRejectFromRequest = async (req) => {
    if (!req?.id || req.resolution) return;

    const confirm = await Swal.fire({
      title: "Rejeitar solicitação?",
      text: req.cod_alerta
        ? `Confirmar rejeição da solicitação ${req.cod_alerta}?`
        : "Confirmar rejeição desta solicitação?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Rejeitar",
      cancelButtonText: "Cancelar",
    });
    if (!confirm.isConfirmed) return;

    try {
      const updated = await guardianAlertService.resolveAlertRequest(
        req.id,
        "rejected"
      );
      upsertRequestOverride({ ...updated, resolution: "rejected" });
      setDetailRequest(null);
      await Swal.fire({
        title: "Solicitação rejeitada",
        icon: "success",
        confirmButtonText: "OK",
      });
    } catch (error) {
      console.error("Erro ao rejeitar solicitação:", error);
      await Swal.fire({
        title: "Não foi possível rejeitar",
        text: "Tente novamente em instantes.",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const canViewIntel = Boolean(user);

  if (!canViewIntel) {
    return <ContentWrapper />;
  }

  return (
    <ContentWrapper>
      <ProjectContent>
        <ProjectTitle>
          <StyledSpan>
            {displayProject?.title === "default"
              ? "Projeto sem Título"
              : displayProject?.title || "Projeto sem título"}
          </StyledSpan>
        </ProjectTitle>

        {hubLoading ? (
          <HubStatusBanner>Carregando hub do projeto…</HubStatusBanner>
        ) : null}

        {hubError ? (
          <HubStatusBanner>
            Não foi possível carregar o hub consolidado. Exibindo dados locais
            do projeto.
            {hubError?.response?.status === 404
              ? " (projeto não encontrado ou sem acesso)"
              : ""}
          </HubStatusBanner>
        ) : null}

        {/* 1. Dados do projeto */}
        <HubSectionBalloon>
          <SubSectionHeader style={{ marginTop: 0 }}>
            <ListItemText>Dados do projeto</ListItemText>
          </SubSectionHeader>

          {summaryCards.length > 0 ? (
            <InfoCardsGrid>
              {summaryCards.map((card) => (
                <InfoCard key={card.key}>
                  <InfoCardLabel>{card.label}</InfoCardLabel>
                  <InfoCardValue>{card.value}</InfoCardValue>
                </InfoCard>
              ))}
            </InfoCardsGrid>
          ) : null}

          <InfoCardsGrid style={{ marginTop: summaryCards.length ? "12px" : 0 }}>
            <InfoCard>
              <InfoCardLabel>Proprietário</InfoCardLabel>
              <InfoCardValue>
                {projectOwner?.full_name || "Não informado"}
              </InfoCardValue>
            </InfoCard>

            <InfoCard>
              <InfoCardLabel>Pessoa</InfoCardLabel>
              <InfoCardValue>
                {projectOwner?.person_type || "Não informado"}
              </InfoCardValue>
            </InfoCard>

            <InfoCard>
              <InfoCardLabel>Documento</InfoCardLabel>
              <InfoCardValue>
                {getProjectOwnerDocumento(projectOwner) || "Não informado"}
              </InfoCardValue>
            </InfoCard>

            <InfoCard>
              <InfoCardLabel>Telefone</InfoCardLabel>
              <InfoCardValue>
                {projectOwner?.phone ? (
                  <a href={`tel:${projectOwner.phone}`}>
                    {convertPhone(projectOwner.phone)}
                  </a>
                ) : (
                  "Não informado"
                )}
              </InfoCardValue>
            </InfoCard>

            <InfoCard>
              <InfoCardLabel>Email</InfoCardLabel>
              <InfoCardValue>
                {projectOwner?.email ? (
                  <a
                    href={buildProjectContactMailtoHref(projectOwner.email, {
                      projectTitle,
                      personName: projectOwner.full_name || projectOwner.name || "",
                    })}
                    title={`Enviar e-mail para ${
                      projectOwner.full_name || projectOwner.email
                    }`}
                  >
                    ✉️ {projectOwner.email}
                  </a>
                ) : (
                  "Não informado"
                )}
              </InfoCardValue>
            </InfoCard>
          </InfoCardsGrid>
        </HubSectionBalloon>

        {/* 2. Localização (endereço + mapa) — topo após dados */}
        <HubSectionBalloon>
          <SubSectionHeader style={{ marginTop: 0 }}>
            <ListItemText>Localização</ListItemText>
          </SubSectionHeader>
          <InfoItem>
            <ListItemText>Endereço</ListItemText>
            <ListItemTextResposta>
              {displayProject?.address || "Não informado"}
            </ListItemTextResposta>
          </InfoItem>
          <div style={{ marginTop: "0.85rem" }}>
            <ProjectHubMapSection
              map={hubMap}
              geographicCoordinates={geographicCoordinates}
              loadingCode={loadingMapCode}
              onPointCodeClick={handleMapPointCodeClick}
            />
          </div>
        </HubSectionBalloon>

        {/* 3. Participantes do projeto — topo após dados */}
        {team.length > 0 ? (
          <HubSectionBalloon>
            <SubSectionHeader style={{ marginTop: 0 }}>
              <ListItemText>
                Participantes do projeto (
                {hub?.people?.team_count ?? team.length})
              </ListItemText>
            </SubSectionHeader>
            <HubTeamGrid>
              {team.map((member) => {
                const person = member?.user || {};
                const key =
                  person.id || person.email || member.roles?.join("-");
                return (
                  <HubTeamCard key={key}>
                    <HubTeamName>
                      {person.full_name || "Sem nome"}
                    </HubTeamName>
                    <HubTeamRoles>
                      {formatHubRoleLabels(member.roles)}
                    </HubTeamRoles>
                    <HubTeamMeta>
                      {person.email ? (
                        <div>
                          <a
                            href={buildProjectContactMailtoHref(person.email, {
                              projectTitle,
                              areaName: Array.isArray(member.areas)
                                ? member.areas
                                    .map((a) => a.name || a.id_code)
                                    .filter(Boolean)
                                    .join(", ")
                                : "",
                              personName: person.full_name || "",
                            })}
                            title={`Enviar e-mail para ${
                              person.full_name || person.email
                            }`}
                          >
                            ✉️ {person.email}
                          </a>
                        </div>
                      ) : null}
                      {person.phone ? (
                        <div>
                          <a
                            href={`tel:${person.phone}`}
                            title={`Ligar para ${
                              person.full_name || person.phone
                            }`}
                          >
                            📞 {convertPhone(person.phone)}
                          </a>
                        </div>
                      ) : null}
                      {Array.isArray(member.areas) && member.areas.length > 0 ? (
                        <div>
                          Áreas:{" "}
                          {member.areas
                            .map((a) => a.name || a.id_code)
                            .filter(Boolean)
                            .join(", ")}
                        </div>
                      ) : null}
                    </HubTeamMeta>
                  </HubTeamCard>
                );
              })}
            </HubTeamGrid>
          </HubSectionBalloon>
        ) : null}

        {/* 4. Áreas do Guardião — links */}
        <HubSectionBalloon>
          <SubSectionHeader style={{ marginTop: 0 }}>
            <ListItemText>
              Áreas do Guardião
              {areaInfo?.areaCount != null ? ` (${areaInfo.areaCount})` : ""}
            </ListItemText>
          </SubSectionHeader>

          {hasAreas ? (
            <HubAreasLinkList>
              {displayProject.guardian_areas.map((area, index) => {
                const idCode = area.id_code || area.id;
                const label =
                  area.name ||
                  area.id_code ||
                  `Área ${index + 1}`;

                if (!idCode) {
                  return (
                    <HubAreaLink as="div" key={`area-no-id-${index}`}>
                      <span>{label}</span>
                      <span>Sem código</span>
                    </HubAreaLink>
                  );
                }

                return (
                  <HubAreaBlock key={idCode}>
                    <HubAreaLink as={Link} to={`/area_details/${idCode}`}>
                      <span>
                        {label}
                        {area.archived ? " (arquivada)" : ""}
                      </span>
                      <span>Ver área</span>
                    </HubAreaLink>
                    <AreaCameraCarousel areaId={idCode} />
                  </HubAreaBlock>
                );
              })}
            </HubAreasLinkList>
          ) : (
            <NoAreasMessage>
              Este projeto não possui áreas Guardian cadastradas.
            </NoAreasMessage>
          )}
        </HubSectionBalloon>

        {/* 5. Detalhes das áreas (sem KML FIRMS — vem na seção seguinte) */}
        <AreasContainer>
          {hasAreas ? (
            <HubSectionBalloon>
              <SubSectionHeader style={{ marginTop: 0 }}>
                <ListItemText>
                  Detalhes das áreas ({areaInfo.areaCount} área
                  {areaInfo.areaCount > 1 ? "s" : ""})
                </ListItemText>
              </SubSectionHeader>

              {displayProject.guardian_areas.map((area, index) => {
                const areaDownloadGroups = groupAreaHubDownloads(
                  area.hub_downloads,
                  area.name
                )
                  .map((group) =>
                    group.key === "kml"
                      ? {
                          ...group,
                          items: group.items.filter(
                            (item) =>
                              !String(item.title || "")
                                .toLowerCase()
                                .includes("firms")
                          ),
                        }
                      : group
                  )
                  .filter((group) => group.items.length > 0);
                const idCode = area.id_code || area.id;

                return (
                  <div key={idCode || index}>
                    <AreaHeader>
                      <AreaName>
                        {idCode ? (
                          <Link
                            to={`/area_details/${idCode}`}
                            style={{ color: "inherit", textDecoration: "none" }}
                          >
                            Área {index + 1}: {area.name}
                          </Link>
                        ) : (
                          <>
                            Área {index + 1}: {area.name}
                          </>
                        )}
                      </AreaName>
                      {area.archived && <ArchivedBadge>Arquivada</ArchivedBadge>}
                    </AreaHeader>

                    <ResponsiblesContainer>
                      {area.vert_responsible_name && (
                        <ResponsibleItem>
                          <ResponsibleLabel>Responsável VERT</ResponsibleLabel>
                          <ResponsibleValue>
                            {area.vert_responsible_name}
                          </ResponsibleValue>
                          {area.vert_responsible_contact?.email ? (
                            <HubContactLine>
                              <a
                                href={buildProjectContactMailtoHref(
                                  area.vert_responsible_contact.email,
                                  {
                                    projectTitle,
                                    areaName: area.name || "",
                                    personName:
                                      area.vert_responsible_name || "",
                                  }
                                )}
                                title={`Enviar e-mail para ${
                                  area.vert_responsible_name ||
                                  area.vert_responsible_contact.email
                                }`}
                              >
                                ✉️ {area.vert_responsible_contact.email}
                              </a>
                            </HubContactLine>
                          ) : null}
                          {area.vert_responsible_contact?.phone ? (
                            <HubContactLine>
                              <a
                                href={`tel:${area.vert_responsible_contact.phone}`}
                                title={`Ligar para ${
                                  area.vert_responsible_name ||
                                  area.vert_responsible_contact.phone
                                }`}
                              >
                                📞{" "}
                                {convertPhone(
                                  area.vert_responsible_contact.phone
                                )}
                              </a>
                            </HubContactLine>
                          ) : null}
                        </ResponsibleItem>
                      )}

                      {area.adi_responsible_name && (
                        <ResponsibleItem>
                          <ResponsibleLabel>Gestor</ResponsibleLabel>
                          <ResponsibleValue>
                            {area.adi_responsible_name}
                          </ResponsibleValue>
                          {area.adi_responsible_contact?.email ? (
                            <HubContactLine>
                              <a
                                href={buildProjectContactMailtoHref(
                                  area.adi_responsible_contact.email,
                                  {
                                    projectTitle,
                                    areaName: area.name || "",
                                    personName: area.adi_responsible_name || "",
                                  }
                                )}
                                title={`Enviar e-mail para ${
                                  area.adi_responsible_name ||
                                  area.adi_responsible_contact.email
                                }`}
                              >
                                ✉️ {area.adi_responsible_contact.email}
                              </a>
                            </HubContactLine>
                          ) : null}
                          {area.adi_responsible_contact?.phone ? (
                            <HubContactLine>
                              <a
                                href={`tel:${area.adi_responsible_contact.phone}`}
                                title={`Ligar para ${
                                  area.adi_responsible_name ||
                                  area.adi_responsible_contact.phone
                                }`}
                              >
                                📞{" "}
                                {convertPhone(
                                  area.adi_responsible_contact.phone
                                )}
                              </a>
                            </HubContactLine>
                          ) : null}
                        </ResponsibleItem>
                      )}

                      {area.total_area && (
                        <ResponsibleItem>
                          <ResponsibleLabel>Área Total</ResponsibleLabel>
                          <ResponsibleValue>
                            {transformNumbersToHectares(area.total_area)} ha
                          </ResponsibleValue>
                        </ResponsibleItem>
                      )}
                    </ResponsiblesContainer>

                    <AreaInfoGrid>
                      {area.matricula_status && (
                        <AreaInfoCard>
                          <AreaInfoLabel>Status da Matrícula</AreaInfoLabel>
                          <AreaInfoValue>{area.matricula_status}</AreaInfoValue>
                        </AreaInfoCard>
                      )}

                      {area.reserve_legal_status && (
                        <AreaInfoCard>
                          <AreaInfoLabel>
                            Situação da Reserva Legal
                          </AreaInfoLabel>
                          <AreaInfoValue
                            color={
                              area.reserve_legal_status
                                ?.toLowerCase()
                                .includes("deficit") ||
                              area.reserve_legal_status
                                ?.toLowerCase()
                                .includes("déficit")
                                ? "var(--danger)"
                                : area.reserve_legal_status
                                    ?.toLowerCase()
                                    .includes("em dia")
                                ? "var(--success)"
                                : undefined
                            }
                          >
                            {area.reserve_legal_status}
                          </AreaInfoValue>
                        </AreaInfoCard>
                      )}

                      {area.sicar_code && (
                        <AreaInfoCard>
                          <AreaInfoLabel>Código SICAR (CAR)</AreaInfoLabel>
                          <AreaInfoValue style={{ fontFamily: "monospace" }}>
                            {formatSICARCode(area.sicar_code)}
                          </AreaInfoValue>
                        </AreaInfoCard>
                      )}

                      {area.status_car && (
                        <AreaInfoCard>
                          <AreaInfoLabel>Status do CAR</AreaInfoLabel>
                          <AreaInfoValue
                            color={
                              area.status_car?.toLowerCase().includes("ativo")
                                ? "var(--success)"
                                : area.status_car
                                    ?.toLowerCase()
                                    .includes("inativo")
                                ? "var(--danger)"
                                : undefined
                            }
                          >
                            {area.status_car}
                          </AreaInfoValue>
                        </AreaInfoCard>
                      )}

                      {area.georeferencing_status && (
                        <AreaInfoCard>
                          <AreaInfoLabel>
                            Status do Georreferenciamento
                          </AreaInfoLabel>
                          <AreaInfoValue>
                            {area.georeferencing_status}
                          </AreaInfoValue>
                        </AreaInfoCard>
                      )}

                      {area.legal_reserve_area && (
                        <AreaInfoCard>
                          <AreaInfoLabel>Área de Reserva Legal</AreaInfoLabel>
                          <AreaInfoValue>
                            {transformNumbersToHectares(area.legal_reserve_area)}{" "}
                            ha
                          </AreaInfoValue>
                        </AreaInfoCard>
                      )}
                    </AreaInfoGrid>

                    {areaDownloadGroups.length > 0 ? (
                      <div style={{ marginTop: "0.75rem" }}>
                        {areaDownloadGroups.map((group) => (
                          <div key={`${idCode}-${group.key}`}>
                            <HubDownloadCategoryTitle>
                              {group.label}
                            </HubDownloadCategoryTitle>
                            <HubDownloadsList>
                              {group.items.map((item) => (
                                <HubDownloadLink
                                  key={item.key}
                                  href={item.href}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <span>{item.title}</span>
                                  <span>{item.badge}</span>
                                </HubDownloadLink>
                              ))}
                            </HubDownloadsList>
                          </div>
                        ))}
                      </div>
                    ) : null}

                    {index < displayProject.guardian_areas.length - 1 && (
                      <AreaSeparator />
                    )}
                  </div>
                );
              })}
            </HubSectionBalloon>
          ) : null}
        </AreasContainer>

        {/* 6. Áreas FIRMS + KML */}
        {hasAreas &&
        displayProject.guardian_areas.some(
          (area) =>
            (Array.isArray(area.firms_areas) && area.firms_areas.length > 0) ||
            (Array.isArray(area.hub_downloads?.firms_kml_urls) &&
              area.hub_downloads.firms_kml_urls.length > 0)
        ) ? (
          <HubSectionBalloon>
            <SubSectionHeader style={{ marginTop: 0 }}>
              <ListItemText>Áreas FIRMS</ListItemText>
            </SubSectionHeader>
            {displayProject.guardian_areas.map((area) => {
              const firmsAreas = Array.isArray(area.firms_areas)
                ? area.firms_areas
                : [];
              const legacyKmls = Array.isArray(area.hub_downloads?.firms_kml_urls)
                ? area.hub_downloads.firms_kml_urls
                : [];

              if (firmsAreas.length === 0 && legacyKmls.length === 0) {
                return null;
              }

              return (
                <div
                  key={`firms-${area.id_code || area.id || area.name}`}
                  style={{ marginBottom: "1rem" }}
                >
                  <HubDownloadCategoryTitle>
                    Guardian: {area.name || area.id_code || "Área"}
                  </HubDownloadCategoryTitle>

                  {firmsAreas.length > 0 ? (
                    <HubDownloadsList>
                      {firmsAreas.map((fa, fi) => {
                        const kmlUrl =
                          fa.kml_file_url ||
                          fa.map_layer?.kml_file_url ||
                          null;
                        const name =
                          fa.name ||
                          fa.map_layer?.name ||
                          `Monitor FIRMS ${fi + 1}`;
                        return (
                          <HubDownloadLink
                            key={fa.id || fa.firms_area_id || `${name}-${fi}`}
                            as={kmlUrl ? "a" : "div"}
                            href={kmlUrl || undefined}
                            target={kmlUrl ? "_blank" : undefined}
                            rel={kmlUrl ? "noopener noreferrer" : undefined}
                            style={
                              !kmlUrl
                                ? { cursor: "default", opacity: 0.85 }
                                : undefined
                            }
                          >
                            <span>{name}</span>
                            <span>{kmlUrl ? "KML" : "Sem KML"}</span>
                          </HubDownloadLink>
                        );
                      })}
                    </HubDownloadsList>
                  ) : (
                    <HubDownloadsList>
                      {legacyKmls.map((url, i) => (
                        <HubDownloadLink
                          key={`${area.id_code}-firms-kml-${i}`}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <span>
                            KML FIRMS — {area.name}
                            {legacyKmls.length > 1 ? ` (${i + 1})` : ""}
                          </span>
                          <span>KML</span>
                        </HubDownloadLink>
                      ))}
                    </HubDownloadsList>
                  )}
                </div>
              );
            })}
          </HubSectionBalloon>
        ) : null}

        {alerts.length > 0 ? (
          <HubSectionBalloon>
            <SubSectionHeader style={{ marginTop: 0 }}>
              <ListItemText>
                Alertas do projeto ({alerts.length})
              </ListItemText>
            </SubSectionHeader>
            <HubListTableScroll>
            <HubListTable>
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Área</th>
                  <th>Tipo</th>
                  <th>Severidade</th>
                  <th>Criado em</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {alerts.map((alert) => {
                  return (
                    <tr key={alert.id || alert.cod_alerta}>
                      <td>
                        {alert.cod_alerta ? (
                          <MapCodeButton
                            type="button"
                            title="Abrir alerta"
                            disabled={Boolean(loadingMapCode)}
                            onClick={() =>
                              handleMapPointCodeClick({
                                label: alert.cod_alerta,
                                source: "alert",
                              })
                            }
                          >
                            {loadingMapCode === alert.cod_alerta
                              ? "Abrindo…"
                              : alert.cod_alerta}
                          </MapCodeButton>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>{alert.guardian_area_name || "—"}</td>
                      <td>
                        {alert.alert_type?.name ||
                          alert.alert_type ||
                          "—"}
                      </td>
                      <td>
                        {alert.severity_level?.name ||
                          alert.severity_level ||
                          "—"}
                      </td>
                      <td>{formatHubDateTime(alert.created_at)}</td>
                      <td>
                        <HubBadge $tone={alert.respondido ? "success" : undefined}>
                          {alert.respondido ? "Respondido" : "Aberto"}
                        </HubBadge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </HubListTable>
            </HubListTableScroll>
          </HubSectionBalloon>
        ) : null}

        {alertRequests.length > 0 ? (
          <HubSectionBalloon>
            <SubSectionHeader style={{ marginTop: 0 }}>
              <ListItemText>
                Solicitações de alerta ({alertRequests.length}
                {pendingRequests.length > 0
                  ? ` · ${pendingRequests.length} pendente(s)`
                  : ""}
                )
              </ListItemText>
            </SubSectionHeader>
            <HubListTableScroll>
            <HubListTable>
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Área</th>
                  <th>Origem</th>
                  <th>Solicitado em</th>
                  <th>Resolução</th>
                </tr>
              </thead>
              <tbody>
                {alertRequests.map((req) => {
                  const resolution = req.resolution;
                  return (
                    <tr key={req.id || req.cod_alerta}>
                      <td>
                        <MapCodeButton
                          type="button"
                          title="Abrir solicitação de alerta"
                          onClick={() => setDetailRequest(req)}
                        >
                          {req.cod_alerta || "Sem código"}
                        </MapCodeButton>
                      </td>
                      <td>
                        {req.area_detail?.name ||
                          req.area?.name ||
                          req.guardian_area_name ||
                          "—"}
                      </td>
                      <td>
                        {req.request_source === "firms_automated"
                          ? "FIRMS"
                          : "Manual"}
                      </td>
                      <td>{formatHubDateTime(req.requested_at)}</td>
                      <td>
                        <AlertRequestResolutionBadge
                          resolution={resolution}
                          opening={openingAlertId === req.id}
                          onOpenAlert={
                            resolution === "created"
                              ? () => handleOpenCreatedAlert(req)
                              : undefined
                          }
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </HubListTable>
            </HubListTableScroll>
          </HubSectionBalloon>
        ) : null}

        <HubSectionBalloon>
          <SubSectionHeader style={{ marginTop: 0 }}>
            <ListItemText>Informações do Projeto</ListItemText>
          </SubSectionHeader>

          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            <InfoItem>
              <ListItemText>
                Possui dívida federal pelo não pagamento de tributos?
              </ListItemText>
              <ListItemTextResposta>
                {returnYesorNoforBoolean(displayProject?.has_federal_debt) ||
                  "Não informado"}
              </ListItemTextResposta>
            </InfoItem>

            <InfoItem>
              <ListItemText>
                Possui Unidade de Conservação (UC) no imóvel?
              </ListItemText>
              <ListItemTextResposta>
                {displayProject?.conservation_unit || "Não informado"}
              </ListItemTextResposta>
            </InfoItem>

            <InfoItem>
              <ListItemText>
                Existem ações tomadas pelo proprietário para garantir a
                preservação das florestas existentes no imóvel?
              </ListItemText>
              <ListItemTextResposta>
                {displayProject?.owner_actions_to_preserve_forest ||
                  "Não informado"}
              </ListItemTextResposta>
            </InfoItem>
          </div>
        </HubSectionBalloon>

        {/* Últimos balões: Relatórios → Downloads */}
        {reportItems.length > 0 ? (
          <HubSectionBalloon>
            <SubSectionHeader style={{ marginTop: 0 }}>
              <ListItemText>Relatórios de alertas</ListItemText>
            </SubSectionHeader>
            <HubDownloadsList>
              {reportItems.map((item) => (
                <HubDownloadLink
                  key={item.key}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span>{item.title}</span>
                  <span>{item.badge || "PDF"}</span>
                </HubDownloadLink>
              ))}
            </HubDownloadsList>
          </HubSectionBalloon>
        ) : null}

        {downloadGroups.length > 0 ? (
          <HubSectionBalloon>
            <SubSectionHeader style={{ marginTop: 0 }}>
              <ListItemText>Downloads</ListItemText>
            </SubSectionHeader>
            {downloadGroups.map((group) => (
              <div key={group.key}>
                <HubDownloadCategoryTitle>{group.label}</HubDownloadCategoryTitle>
                <HubDownloadsList>
                  {group.items.map((item) => (
                    <HubDownloadLink
                      key={item.key}
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <span>{item.title}</span>
                      <span>{item.badge}</span>
                    </HubDownloadLink>
                  ))}
                </HubDownloadsList>
              </div>
            ))}
          </HubSectionBalloon>
        ) : null}
      </ProjectContent>

      {isPasswordModalOpen && (
        <PasswordModal
          onConfirm={(pwd) => {
            setPassword(pwd);
            closePasswordModal();
          }}
        />
      )}

      {detailRequest ? (
        <RequestModal
          req={detailRequest}
          onClose={() => setDetailRequest(null)}
          canCreateAlert={showCreateAlert}
          viewingAlert={openingAlertId === detailRequest.id}
          onViewAlert={async (req) => {
            await handleOpenCreatedAlert(req);
          }}
          onCreateAlert={handleCreateAlertFromRequest}
          onReject={handleRejectFromRequest}
          onMarkedReceived={(updated) => {
            upsertRequestOverride(updated);
          }}
        />
      ) : null}
    </ContentWrapper>
  );
};

export default Intel;
